import { tasksApi } from "@/modules/tasks/api/tasksApi";
import { normalizeTaskPayload } from "@/modules/tasks/domain/normalizeTaskPayload.mjs";
import { acceptEvent, removeTask, upsertTask } from "@/modules/tasks/domain/taskCollection.mjs";
import { createTaskSubscription } from "@/modules/tasks/realtime/createTaskSubscription.mjs";
import { getEcho, initializeWebSocket } from "@/shared/realtime/websocket";

let dispatchToStore = null;
const subscription = createTaskSubscription({
  initialize: initializeWebSocket,
  getEcho,
  onEvent: (event) => dispatchToStore?.("tasks/applyRealtime", event),
  onReconnect: () => dispatchToStore?.("tasks/resynchronize"),
});

export default {
  namespaced: true,
  state: () => ({ active: [], mine: [], mineParams: {}, loading: false, seenEventIds: [] }),
  getters: {
    active: (state) => state.active,
    mine: (state) => state.mine,
    loading: (state) => state.loading,
  },
  mutations: {
    setLoading(state, value) { state.loading = value; },
    replaceActive(state, tasks) { state.active = tasks; },
    replaceMine(state, tasks) { state.mine = tasks; },
    setMineParams(state, params) { state.mineParams = { ...params }; },
    upsertActive(state, task) { state.active = upsertTask(state.active, task); },
    upsertMine(state, task) { state.mine = upsertTask(state.mine, task); },
    removeActive(state, payload) { state.active = removeTask(state.active, payload.id, payload.version); },
    removeMine(state, payload) { state.mine = removeTask(state.mine, payload.id, payload.version); },
  },
  actions: {
    async fetchActive({ commit }) {
      commit("setLoading", true);
      try {
        const response = await tasksApi.active();
        commit("replaceActive", response.data.map((task) => normalizeTaskPayload(task).task));
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchMine({ commit }, params = {}) {
      commit("setLoading", true);
      commit("setMineParams", params);
      try {
        const response = await tasksApi.mine(params);
        commit("replaceMine", response.data.map((task) => normalizeTaskPayload(task).task));
      } finally {
        commit("setLoading", false);
      }
    },
    async startRealtime({ dispatch }) {
      dispatchToStore = (type, payload) => dispatch(type, payload, { root: true });
      await subscription.start();
    },
    stopRealtime() { subscription.stop(); },
    async resynchronize({ state, dispatch }) {
      await Promise.all([dispatch("fetchActive"), dispatch("fetchMine", state.mineParams)]);
    },
    async applyRealtime({ state, rootState, commit, dispatch }, raw) {
      let envelope;
      try {
        envelope = normalizeTaskPayload(raw);
      } catch (error) {
        if (error.code === "TASK_SCHEMA_UNSUPPORTED") dispatch("resynchronize");
        return;
      }
      if (!acceptEvent(state.seenEventIds, envelope.event_id)) return;

      const payload = { id: envelope.task_id ?? envelope.task?.id, version: envelope.version };
      if (envelope.action === "deleted" || !envelope.task) {
        commit("removeActive", payload);
        await dispatch("fetchMine", state.mineParams);
        return;
      }

      const task = envelope.task;
      if (task.status === "zakończone") commit("removeActive", payload);
      else commit("upsertActive", task);

      if (rootState.user?.id) await dispatch("fetchMine", state.mineParams);
    },
  },
};
